import { type Keyframes, keyframes } from '@emotion/react';
import { alertClasses } from '@mui/material/Alert';
import { buttonClasses } from '@mui/material/Button';
import { chipClasses } from '@mui/material/Chip';
import CssBaseline from '@mui/material/CssBaseline';
import { formHelperTextClasses } from '@mui/material/FormHelperText';
import GlobalStyles from '@mui/material/GlobalStyles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import {
  type CommonColors,
  createTheme,
  ThemeProvider as MuiThemeProvider,
  type PaletteColorOptions,
  type Theme,
} from '@mui/material/styles';
import { tabClasses } from '@mui/material/Tab';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import React from 'react';

const Book = new URL('./fonts/CircularXXWeb-Book.woff2', import.meta.url);
const BookBold = new URL('./fonts/CircularXXWeb-Bold.woff2', import.meta.url);
const BookItalic = new URL('./fonts/CircularXXWeb-Italic.woff2', import.meta.url);
const BookBoldItalic = new URL('./fonts/CircularXXWeb-BoldItalic.woff2', import.meta.url);

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white: true;
  }

  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    text: true;
  }
}

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    primary: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    primary: true;
  }
}

declare module '@mui/material/Switch' {
  interface SwitchPropsColorOverrides {
    contrast: true;
  }
}

// Note: This theme uses numbered scales (100, 200, 300, etc.) instead of light/dark properties
// To prevent light/dark usage, use ESLint rules or code review processes

declare module '@mui/material/styles' {
  interface Theme {
    constants: Record<string, number>;
    animations: Record<string, Keyframes>;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    constants?: Record<string, number>;
    animations?: Record<string, Keyframes>;
  }

  // Update palette to use numbered scales
  interface PaletteColor {
    /**
     * @deprecated The color should not be used
     */

    light: string;
    /**
     * @deprecated The color should not be used
     */
    dark: string;
    [key: number]: string;
    main: string;
    contrastText: string;
  }

  interface SimplePaletteColorOptions {
    /**
     * @deprecated The method should not be used
     */

    light?: string | undefined;
    [key: number]: string;
  }

  interface Palette {
    primary: PaletteColor;
    warning: PaletteColor;
    error: PaletteColor;
    success: PaletteColor;
    neutral: PaletteColor;
    border: PaletteColor;
    chart: PaletteColor;
    info: PaletteColor;
    layout: PaletteColor;
    gray: PaletteColor;
    ice: PaletteColor;
    aqua: PaletteColor;
    orange: PaletteColor;
    /**
     * @deprecated The method should not be used
     */
    common: CommonColors;
  }

  interface PaletteOptions {
    primary?: PaletteColorOptions;
    warning?: PaletteColorOptions;
    error?: PaletteColorOptions;
    success?: PaletteColorOptions;
    neutral?: PaletteColorOptions;
    border?: PaletteColorOptions;
    chart?: PaletteColorOptions;
    info?: PaletteColorOptions;
    layout?: PaletteColorOptions;
    gray?: PaletteColorOptions;
    ice?: PaletteColorOptions;
    aqua?: PaletteColorOptions;
    orange?: PaletteColorOptions;
    /**
     * @deprecated The method should not be used
     */
    common?: Partial<CommonColors>;
  }
}

const theme = createTheme({
  animations: {
    wiggle: keyframes`
    0%, 100% {
      transform: rotate(0deg);
    }
    
    17%, 50%, 84% {
      transform: rotate(5deg);
    }
    
    35%, 67% {
      transform: rotate(-5deg);
    `,
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontSize: '16px',
          [`.${alertClasses.action}`]: {
            paddingTop: 0,
          },
        },
      },
      variants: [
        {
          props: { severity: 'info' },
          style: ({ theme }) => ({
            '& .MuiAlert-icon': {
              color: theme.palette.info[200],
            },
            '& p, span': {
              color: theme.palette.info[300],
            },
            backgroundColor: theme.palette.info[100],
            color: theme.palette.info[300],
          }),
        },
        {
          props: { severity: 'success' },
          style: ({ theme }) => ({
            '& .MuiAlert-icon': {
              color: theme.palette.success[200],
            },
            backgroundColor: theme.palette.success[100],
            color: theme.palette.success[300],
          }),
        },
        {
          props: { severity: 'error' },
          style: ({ theme }) => ({
            '& .MuiAlert-icon': {
              color: theme.palette.error[200],
            },
            backgroundColor: theme.palette.error[100],
            color: theme.palette.error[300],
          }),
        },
        {
          props: { severity: 'warning' },
          style: ({ theme }) => ({
            '& .MuiAlert-icon': {
              color: theme.palette.warning[200],
            },
            backgroundColor: theme.palette.warning[100],
            color: theme.palette.warning[300],
          }),
        },
        {
          props: { severity: 'primary' },
          style: ({ theme }) => ({
            '& .MuiAlert-icon': {
              color: theme.palette.primary.main,
            },
            '& p, span': {
              color: theme.palette.primary[400],
            },
            backgroundColor: theme.palette.primary[100],
            color: theme.palette.primary[400],
          }),
        },
      ],
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.common.white,
          borderBottom: `2px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          zIndex: theme.zIndex.drawer + 1,
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: () => ({
          '&:hover': {
            backgroundColor: theme.palette.layout[100],
          },
          minWidth: 'min-content',
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }),
      },
      variants: [
        {
          props: { color: 'secondary', variant: 'outlined' },
          style: ({ theme }) => ({
            '&:hover': {
              backgroundColor: theme.palette.neutral[20],
              border: `1px solid ${theme.palette.neutral[10]}`,
            },
            backgroundColor: theme.palette.layout[100],
            border: `1px solid ${theme.palette.neutral[10]}`,
            color: theme.palette.neutral[100],
          }),
        },
        {
          props: {
            color: 'primary',
            variant: 'contained',
          },
          style: ({ theme }) => ({
            [`&.${buttonClasses.disabled}`]: {
              '&:hover': {
                backgroundColor: theme.palette.neutral[10],
                color: theme.palette.neutral[60],
              },
              backgroundColor: theme.palette.neutral[10],
              color: theme.palette.neutral[60],
            },
            '&:hover': {
              backgroundColor: theme.palette.primary[500],
            },
          }),
        },
        {
          props: {
            color: 'error',
            variant: 'outlined',
          },
          style: ({ theme }) => ({
            '&:hover': {
              backgroundColor: theme.palette.error[100],
              borderColor: theme.palette.error[200],
              color: theme.palette.error[200],
            },
            backgroundColor: theme.palette.layout[100],
            borderColor: theme.palette.error[200],
            color: theme.palette.error[200],
          }),
        },
        {
          props: { variant: 'text' },
          style: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: theme.palette.layout[100],
              color: theme.palette.neutral[100],
            },
            '&:hover': {
              backgroundColor: theme.palette.layout[200],
              border: `1px solid ${theme.palette.neutral[10]}`,
            },
            backgroundColor: 'transparent',
            border: `1px solid transparent`,
            color: theme.palette.neutral[100],
          }),
        },
      ],
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px 12px 0 16px !important',
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { color: 'success' },
          style: ({ theme }) => ({
            [`&.${chipClasses.clickable}`]: {
              '&:hover': {
                backgroundColor: theme.palette.success[200],
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.success[100],
              color: theme.palette.success[300],
            },
            backgroundColor: theme.palette.success[100],
            color: theme.palette.success[300],
          }),
        },
        {
          props: { color: 'primary' },
          style: ({ theme }) => ({
            [`&.${chipClasses.clickable}`]: {
              '&:hover': {
                backgroundColor: theme.palette.primary[200],
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.primary[100],
              color: theme.palette.primary[400],
            },
            backgroundColor: theme.palette.primary[100],
            color: theme.palette.primary[400],
          }),
        },
        {
          props: { color: 'info' },
          style: ({ theme }) => ({
            [`&.${chipClasses.clickable}`]: {
              '&:hover': {
                backgroundColor: theme.palette.info[300],
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.info[100],
              color: theme.palette.info[200],
            },
            backgroundColor: theme.palette.info[100],
            color: theme.palette.info[200],
          }),
        },
        {
          props: { color: 'warning' },
          style: ({ theme }) => ({
            [`&.${chipClasses.clickable}`]: {
              '&:hover': {
                backgroundColor: theme.palette.warning[200],
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.warning[100],
              color: theme.palette.warning[300],
            },
            backgroundColor: theme.palette.warning[100],
            color: theme.palette.warning[300],
          }),
        },
        {
          props: { color: 'error' },
          style: ({ theme }) => ({
            [`&.${chipClasses.clickable}`]: {
              '&:hover': {
                backgroundColor: theme.palette.error[300],
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.error[100],
              color: theme.palette.error[200],
            },
            backgroundColor: theme.palette.error[100],
            color: theme.palette.error[200],
          }),
        },
      ],
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: "CircularXXWeb-Book";
          src: url('${Book.href}') format("woff2");
          font-weight: 400;
          font-style: normal;
      }
      @font-face {
          font-family: "CircularXXWeb-Bold";
          src: url("${BookBold.href}") format("woff2");
          font-weight: 700;
          font-style: normal;
      }
      @font-face {
          font-family: "CircularXXWeb-Italic";
          src: url("${BookItalic.href}") format("woff2");
          font-weight: 400;
          font-style: italic;
      }
      @font-face {
          font-family: "CircularXXWeb-BoldItalic";
          src: url("${BookBoldItalic.href}") format("woff2");
          font-weight: 700;
          font-style: italic;
      }
      `,
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingBottom: theme.spacing(3),
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          paddingTop: 0,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ ownerState, theme }) => {
          if (ownerState.anchor === 'bottom') {
            return {
              borderTopLeftRadius: '40px',
              borderTopRightRadius: '40px',
            };
          }

          if (ownerState.anchor === 'right') {
            return {
              height: `calc(100% - ${theme.constants.groundControlNavbarHeight}px)`,
              top: theme.constants.groundControlNavbarHeight,
            };
          }
        },
      },
    },
    MuiGrid: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-disabled': {
            backgroundColor: theme.palette.layout[100],
          },
          '&:hover': {
            backgroundColor: theme.palette.background.default,
          },
          backgroundColor: theme.palette.layout[100],
        }),
      },
      variants: [
        {
          props: { color: 'primary' },
          style: ({ theme }) => ({
            '&:hover': {
              backgroundColor: theme.palette.primary[500],
            },
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          }),
        },
        {
          props: { color: 'default' },
          style: ({ theme }) => ({
            '&:hover': {
              backgroundColor: theme.palette.layout[300],
            },
            backgroundColor: 'transparent',
          }),
        },
      ],
    },
    MuiImageListItemBar: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            color: theme.palette.primary.main,
          },
          color: theme.palette.text.primary,
          overflow: 'hidden',
          textDecoration: 'underline',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }),
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiSwitch: {
      variants: [
        {
          props: { checked: true },
          style: () => ({
            '& .MuiSwitch-switchBase': {
              top: -2,
            },
          }),
        },
        {
          props: { color: 'primary' },
          style: ({ theme }) => ({
            '& .MuiSvgIcon-root': {
              background: theme.palette.primary[400],
              borderRadius: '50%',
              color: theme.palette.layout[100],
              padding: theme.spacing(0.5),
            },
            '& .MuiSwitch-track': {
              color: theme.palette.primary[500],
            },
          }),
        },
        {
          props: { color: 'contrast' },
          style: ({ theme }) => ({
            '& .MuiSvgIcon-root': {
              background: theme.palette.primary[400],
              borderRadius: '50%',
              color: theme.palette.layout[100],
              padding: theme.spacing(0.5),
            },
            '& .MuiSwitch-track': {
              background: theme.palette.layout[100],
            },
          }),
        },
      ],
    },
    MuiTab: {
      styleOverrides: {
        root: () => ({
          textTransform: 'none',
          [`&.${tabClasses.selected}`]: {
            fontWeight: 'bold',
          },
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& tbody tr:last-of-type td, & tbody tr:last-of-type th': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          WebkitPrintColorAdjust: 'exact',
          backgroundColor: theme.palette.background.default,
          fontWeight: theme.typography.fontWeightBold,
          printColorAdjust: 'exact',
        }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.border.main}`,
          borderRadius: '8px',
          overflow: 'hidden',
          width: '100%',
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
    MuiTabs: {
      defaultProps: {
        scrollButtons: 'auto',
        variant: 'scrollable',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.border.main}`,
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          [`> .${outlinedInputClasses.error}`]: {
            color: theme.palette.error[200],
            [`.${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.error[200] + ' !important',
            },
          },
          [`.${formHelperTextClasses.error}`]: {
            color: theme.palette.error[200] + ' !important',
          },
        }),
      },
    },

    MuiTooltip: {
      defaultProps: {
        placement: 'top',
      },
    },
  },
  constants: {
    footerHeight: 63.5,
    groundControlNavbarHeight: 63,
    sidebarWidth: 150,
  },
  palette: {
    aqua: {
      100: '#3AB8B9',
      40: '#3AB8B9',
      80: '#3AB8B9',
      contrastText: '#fff',
      main: '#3AB8B9',
    },
    background: {
      default: '#f9f9f9',
    },
    border: {
      main: '#E7E7E7',
    },
    chart: {
      main: '#6347FF',
    },
    error: {
      100: '#FEEBEE',
      200: '#F6353E',
      300: '#761527',
      contrastText: '#fff',
      main: '#F6353E',
    },
    gray: {
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      50: '#FAFAFA',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      main: '#9E9E9E',
    },
    ice: {
      100: '#C4DCF4',
      40: '#C4DCF4',
      80: '#C4DCF4',
      contrastText: '#000',
      main: '#C4DCF4',
    },
    info: {
      100: '#E5F6FD',
      200: '#0288D1',
      300: '#014361',
      main: '#0288D1',
    },
    layout: {
      100: '#FFFFFF',
      200: '#F6F6F6',
      300: '#F3F3F3',
      main: '#F3F3F3',
    },
    neutral: {
      10: '#E7E7E8',
      100: '#0D0C1D',
      20: '#CFCED4',
      40: '#9E9EA5',
      5: '#F3F3F4',
      60: '#6E6D77',
      80: '#3D3D4A',
      contrastText: '#fff',
      main: '#6E6D77',
    },
    orange: {
      100: '#FF6248',
      40: '#FF6248',
      80: '#FF6248',
      contrastText: '#fff',
      main: '#FF6248',
    },
    primary: {
      100: '#F6F5FF',
      200: '#E8E3FF',
      300: '#D4BBFF',
      400: '#6347FF',
      500: '#4531B2',
      contrastText: '#fff',
      main: '#6347FF',
    },
    secondary: {
      100: '#f0f0f2',
      300: '#6E6D77',
      500: '#0D0C1D',
      contrastText: '#fff',
      main: '#0D0C1D',
    },
    success: {
      100: '#EAF7ED',
      200: '#2FB554',
      300: '#1B5E20',
      main: '#1B5E20',
    },
    warning: {
      100: '#FFF4E5',
      200: '#FF891C',
      300: '#C4650B',
      main: '#C4650B',
    },
  },
  typography: {
    fontFamily: "'CircularXXWeb-Book', sans-serif",
  },
});

export type { Theme };
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{ body: { WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' } }} />
      {children}
    </MuiThemeProvider>
  );
};
